import { Component, inject, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LoaderService } from 'src/app/modules/loading/loader.service';
import { constants } from '../../constants/constants';
import { BRAND, environment } from '../../../environments/environment';
import {BrandMockDictionary, ObjectBrandMockDictionary} from './brand-mock-login.dictionary';
import { BookingService } from '../../modules/core/services/booking.service';
import { CookieMonitorService } from '../../shared/services/cookie-monitor/cookie-monitor.service';

@Component({
    selector: 'app-mocklogincomponent',
    templateUrl: './mocklogin.component.html',
    styleUrls: ['./mocklogin.component.scss'],
    standalone: false
})
export class MockLoginComponent implements OnInit {
  private readonly bookingService = inject(BookingService);
  private readonly cookieMonitor = inject(CookieMonitorService);

  public loginForm: UntypedFormGroup;
  public cookiesForm: UntypedFormGroup;
  public datePickerLabels: any;
  public sailDateLabels: any;
  public dateFormat: string = constants.DATE_FORMAT.USA;
  public errors: any;
  public cookiesOption = true;
  public formOption = false;
  public brand: string = BRAND;
  public localeArray: string[];
  public charterOptionsString = '';
  public layout = '';
  public selectedCharterOption = '';
  public accessTokenStatus = this.cookieMonitor.accessTokenPresent;
  public olciDataStatus = this.cookieMonitor.olciDataPresent;
  // If you want to enable the GLC feature in the local environment, get the fe_sso cookie value from GA and paste it in the fe_sso field of the cookiesForm
  public feSsoChecked = signal(false);

  constructor(
    private readonly loader: LoaderService,
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router
  ) {
    this.buildForm();
    this.datePickerLabels = {
      main: 'Date of birth',
      month: 'month',
      day: 'day',
      year: 'year',
    };
    this.sailDateLabels = { ...this.datePickerLabels, main: 'Sail date' };
    this.localeArray = [ 'en', 'es', 'pt', 'zh']
  }

  ngOnInit() {
    if(environment?.production){
      window.location.href = constants.ROUTES.GUEST_ACCOUNT;
    }
    this.loader.hideLoader();

    this.charterOptionsString = this.mapCharterOptions();
    this.layout = constants.RADIO_BUTTON_DIRECTION.HORIZONTAL;
  }

  private navigate(): void {
    this.router.navigate([constants.ROUTES.SELECT_GUESTS_FOR_CHECKIN]);
  }

  private buildForm(): void {
    this.loginForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(60),
          Validators.pattern(constants.VALIDATION_REGEX.EMAIL),
        ],
      ],
      dob: ['', [Validators.required]],
      locale: ['', [Validators.required]],
      vdsId: ['', [Validators.required]],
      guestId: ['', [Validators.required]],
      bookingId: ['', [Validators.required]],
      sailDate: ['', [Validators.required]],
      shipCode: ['', [Validators.required]],
      accessToken: ['', [Validators.required]],
      charterSailing: ['', [Validators.required]],
    });
    this.cookiesForm = this.fb.group({
      olciData: ['', [Validators.required]],
      accessToken: ['', [Validators.required]],
      feSso: [''],
    });
  }

  private isValid(form: UntypedFormGroup): boolean {
    for (const control in form.controls) {
      if (!form.controls[control].valid) {
        return false;
      }
    }
    return true;
  }

  private setErrors(form: UntypedFormGroup): void {
    const REQUIRED = 'The information is required';
    const errorsArray = Object.keys(this.errors);
    errorsArray.forEach((key) => {
      if (form.controls[key].invalid) {
        this.errors[key].push(REQUIRED);
      }
    });
  }

  private buildErrorsObject(form: UntypedFormGroup): any {
    this.errors = {};
    for (const control in form.controls) {
      this.errors[control] = [];
    }
    return this.errors;
  }

  public expandCompleteForm(): void {
    this.formOption = !this.formOption;
    this.cookiesOption = false;
    this.errors = {};
  }

  public saveFormValues(): void {
    const isValid = this.isValid(this.loginForm);
    if (!isValid) {
      this.buildErrorsObject(this.loginForm);
      this.setErrors(this.loginForm);
      return;
    }

    const okProperties = [ 'bookingId', 'guestId', 'vdsId', 'dob', 'sailDate', 'email', 'lastName', 'shipCode', 'locale' ];
    const formValues: any = {
      charterSailing: this.loginForm.controls['charterSailing'].value === 'yes' || false,
      accountOwnerName: this.loginForm.controls['firstName'].value + ' ' + this.loginForm.controls['lastName'].value,
      brand: BrandMockDictionary[BRAND as ObjectBrandMockDictionary],
    };
    for (const control in this.loginForm.controls) {
      if (okProperties.includes(control)) {
        formValues[control] = this.loginForm.controls[control].value;
      }
    }
    const encodedData = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.' + window.btoa(JSON.stringify(formValues));
    document.cookie = `accessToken=${this.loginForm.controls['accessToken'].value};path=/;`;
    document.cookie = `olciData=${encodedData};path=/;`;
    this.navigate();
  }

  public expandPasteCookies(): void {
    this.cookiesOption = !this.cookiesOption;
    this.formOption = false;
    this.errors = {};
  }

  public setCookies(): void {
    const isValid = this.isValid(this.cookiesForm);
    if (!isValid) {
      this.buildErrorsObject(this.cookiesForm);
      this.setErrors(this.cookiesForm);
      return;
    }
    document.cookie = `accessToken=${this.cookiesForm.controls['accessToken'].value};path=/;`;
    document.cookie = `olciData=${this.cookiesForm.controls['olciData'].value};path=/;`;
    if (this.cookiesForm.controls['feSso']?.value && this.feSsoChecked()) {
      document.cookie = `fe_sso=${this.cookiesForm.controls['feSso'].value};path=/;`;
    }
    this.navigate();
  }

  public login() {
    this.navigate();
  }

  private mapCharterOptions(): string {
    const charterOptions = [
      {
        value: 'yes',
        id: 'yes',
        label: 'Yes',
        styles: { padding: "5px" }
      },
      {
        value: 'no',
        id: 'no',
        label: 'No',
        styles: { padding: "5px" }
      }
    ];
    return JSON.stringify(charterOptions);
  }

  onCharterOptionSelected(option: any): void {
    this.selectedCharterOption = option.detail;
    this.loginForm.controls['charterSailing'].setValue(this.selectedCharterOption)
		this.loginForm?.controls['charterSailing'].markAsDirty();
  }

  clearCookies(): void {
    this.bookingService.deleteSessionData();
  }

  onFeSsoChecked(): void {
    this.feSsoChecked.update((value) => !value);
  }
}
